import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { B2BDTC_UI_NAMES } from "../CreateRewardsModal/schemas";
import ProductSelect from "./ProductSelect";
import "./b2bdtc-specifications.scss";

const B2BDTCSpecifications = ({
  uiName,
  rewardTypeData = {},
  onChange,
  disabled = false,
}) => {
  const isFreeProduct = uiName === B2BDTC_UI_NAMES.FREE_PRODUCT;
  const isMultiProduct = uiName === B2BDTC_UI_NAMES.MULTI_PRODUCT;
  const isDiscountOffer = uiName === B2BDTC_UI_NAMES.DISCOUNT_OFFER;

  // --- Free Product handlers ---
  const freeProductSelected = useMemo(() => {
    if (!isFreeProduct || !rewardTypeData.product_id) return [];
    return [
      {
        id: rewardTypeData.product_id,
        name: rewardTypeData.product_name || "",
        code: rewardTypeData.product_code || "",
        category: rewardTypeData.product_category || "",
      },
    ];
  }, [isFreeProduct, rewardTypeData]);

  const handleFreeProductSelect = useCallback(
    (product) => {
      onChange({
        ...rewardTypeData,
        product_id: product.id,
        product_code: product.code,
        product_name: product.name,
        product_category: product.category,
      });
    },
    [onChange, rewardTypeData]
  );

  const handleFreeProductRemove = useCallback(() => {
    onChange(
      _.omit(rewardTypeData, [
        "product_id",
        "product_code",
        "product_name",
        "product_category",
      ])
    );
  }, [onChange, rewardTypeData]);

  // --- Multi Product handlers ---
  const multiProductSelected = useMemo(() => {
    if (!isMultiProduct || !Array.isArray(rewardTypeData.products)) return [];
    return rewardTypeData.products.map((p) => ({
      id: p.product_id,
      name: p.product_name || "",
      code: p.product_code || "",
      category: p.product_category || "",
      quantity: p.quantity || 1,
    }));
  }, [isMultiProduct, rewardTypeData]);

  const selectionRule = rewardTypeData.selection_rule || "OR";

  const handleMultiProductSelect = useCallback(
    (product) => {
      const existing = rewardTypeData.products || [];
      const updated = [
        ...existing,
        {
          product_id: product.id,
          product_code: product.code,
          product_name: product.name,
          product_category: product.category,
          quantity: 1,
        },
      ];
      onChange({
        ...rewardTypeData,
        products: updated,
        selection_rule: rewardTypeData.selection_rule || "OR",
      });
    },
    [onChange, rewardTypeData]
  );

  const handleMultiProductRemove = useCallback(
    (productId) => {
      const updated = (rewardTypeData.products || []).filter(
        (p) => p.product_id !== productId
      );
      onChange({
        ...rewardTypeData,
        products: updated,
      });
    },
    [onChange, rewardTypeData]
  );

  const handleQuantityChange = useCallback(
    (productId, quantity) => {
      const qty = Math.max(1, Number(quantity) || 1);
      const updated = (rewardTypeData.products || []).map((p) =>
        p.product_id === productId ? { ...p, quantity: qty } : p
      );
      onChange({ ...rewardTypeData, products: updated });
    },
    [onChange, rewardTypeData]
  );

  const handleRuleChange = useCallback(
    (rule) => {
      onChange({ ...rewardTypeData, selection_rule: rule });
    },
    [onChange, rewardTypeData]
  );

  // --- Discount Offer handlers ---
  const discountProductSelected = useMemo(() => {
    if (!isDiscountOffer || !rewardTypeData.product_id) return [];
    return [
      {
        id: rewardTypeData.product_id,
        name: rewardTypeData.product_name || "",
        code: rewardTypeData.product_code || "",
        category: rewardTypeData.product_category || "",
      },
    ];
  }, [isDiscountOffer, rewardTypeData]);

  const handleDiscountProductSelect = useCallback(
    (product) => {
      onChange({
        ...rewardTypeData,
        product_id: product.id,
        product_code: product.code,
        product_name: product.name,
        product_category: product.category,
      });
    },
    [onChange, rewardTypeData]
  );

  const handleDiscountProductRemove = useCallback(() => {
    onChange(
      _.omit(rewardTypeData, [
        "product_id",
        "product_code",
        "product_name",
        "product_category",
      ])
    );
  }, [onChange, rewardTypeData]);

  const handleDiscountAmountChange = useCallback(
    (e) => {
      const value = e.target.value;
      onChange({
        ...rewardTypeData,
        discount_amount: value === "" ? "" : Number(value),
      });
    },
    [onChange, rewardTypeData]
  );

  const handleDiscountUnitChange = useCallback(
    (unit) => {
      onChange({ ...rewardTypeData, discount_unit: unit });
    },
    [onChange, rewardTypeData]
  );

  useEffect(() => {
    if (isDiscountOffer && !rewardTypeData.discount_unit) {
      onChange({ ...rewardTypeData, discount_unit: "AMOUNT" });
    }
  }, [isDiscountOffer, onChange, rewardTypeData]);

  return (
    <div className="b2bdtc-specifications">
      <h6 className="b2bdtc-specifications-title font-lg font-bold">
        Specifications
      </h6>

      {/* --- Free Product --- */}
      {isFreeProduct && (
        <ProductSelect
          mode="single"
          selectedProducts={freeProductSelected}
          onSelect={handleFreeProductSelect}
          onRemove={handleFreeProductRemove}
          disabled={disabled}
        />
      )}

      {/* --- Multi Product --- */}
      {isMultiProduct && (
        <>
          <ProductSelect
            mode="multi"
            selectedProducts={multiProductSelected}
            onSelect={handleMultiProductSelect}
            onRemove={handleMultiProductRemove}
            disabled={disabled}
          />
          {multiProductSelected.length >= 2 && (
            <div className="b2bdtc-rule-line">
              <span className="b2bdtc-rule-label">Consumer gets</span>
              <div className="b2bdtc-segmented">
                <button
                  type="button"
                  className={`b2bdtc-seg-btn ${selectionRule === "OR" ? "active" : ""}`}
                  onClick={() => handleRuleChange("OR")}
                  disabled={disabled}
                >
                  One of them <b>OR</b>
                </button>
                <button
                  type="button"
                  className={`b2bdtc-seg-btn ${selectionRule === "AND" ? "active" : ""}`}
                  onClick={() => handleRuleChange("AND")}
                  disabled={disabled}
                >
                  All of them <b>AND</b>
                </button>
              </div>
            </div>
          )}
          {multiProductSelected.length > 0 && (
            <div className="b2bdtc-selected-products">
              {multiProductSelected.map((product) => (
                <div key={product.id} className="b2bdtc-selected-row">
                  <div className="b2bdtc-selected-info">
                    <span className="b2bdtc-selected-name">{product.name}</span>
                    <span className="b2bdtc-selected-meta">
                      {product.code}
                      {product.category ? ` \u00B7 ${product.category}` : ""}
                    </span>
                  </div>
                  <div className="b2bdtc-qty-box">
                    <span className="b2bdtc-qty-label">Qty</span>
                    <input
                      type="number"
                      className="b2bdtc-qty-input"
                      value={product.quantity}
                      min={1}
                      onChange={(e) =>
                        handleQuantityChange(product.id, e.target.value)
                      }
                      disabled={disabled}
                      data-testid={`qty-input-${product.id}`}
                    />
                  </div>
                  {!disabled && (
                    <span
                      className="b2bdtc-selected-remove"
                      onClick={() => handleMultiProductRemove(product.id)}
                      data-testid={`remove-product-${product.id}`}
                    >
                      &times;
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* --- Discount Offer --- */}
      {isDiscountOffer && (
        <>
          <ProductSelect
            mode="single"
            selectedProducts={discountProductSelected}
            onSelect={handleDiscountProductSelect}
            onRemove={handleDiscountProductRemove}
            disabled={disabled}
          />
          <div className="b2bdtc-discount-row">
            <div className="b2bdtc-discount-field">
              <div className="b2bdtc-field-header">
                <span className="b2bdtc-field-label">Discount Amount</span>
                <span className="b2bdtc-field-required">*Required</span>
              </div>
              <input
                type="number"
                className="b2bdtc-discount-input"
                value={rewardTypeData.discount_amount ?? ""}
                onChange={handleDiscountAmountChange}
                placeholder="Enter amount"
                min={0}
                disabled={disabled}
                data-testid="discount-amount-input"
              />
            </div>
            <div className="b2bdtc-discount-field">
              <div className="b2bdtc-field-header">
                <span className="b2bdtc-field-label">Discount Type</span>
                <span className="b2bdtc-field-required">*Required</span>
              </div>
              <DiscountTypeSelect
                value={rewardTypeData.discount_unit || "AMOUNT"}
                onChange={handleDiscountUnitChange}
                disabled={disabled}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DISCOUNT_TYPE_OPTIONS = [
  { value: "AMOUNT", label: "Discount amount (TL)" },
  { value: "PERCENTAGE", label: "Percentage (%)" },
];

const DiscountTypeSelect = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedLabel = useMemo(
    () => DISCOUNT_TYPE_OPTIONS.find((o) => o.value === value)?.label || "",
    [value]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="b2bdtc-discount-type-select" ref={containerRef}>
      <div
        className={`b2bdtc-control ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        data-testid="discount-type-control"
      >
        <span
          className={`b2bdtc-control-value ${!selectedLabel ? "placeholder" : ""}`}
        >
          {selectedLabel || "Select Discount Type"}
        </span>
        <span className="b2bdtc-control-chevron">&#9662;</span>
      </div>
      {open && (
        <div
          className="b2bdtc-menu b2bdtc-discount-type-menu"
          data-testid="discount-type-menu"
        >
          <div className="b2bdtc-menu-list">
            {DISCOUNT_TYPE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={`b2bdtc-menu-option simple ${opt.value === value ? "selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                data-testid={`discount-type-${opt.value}`}
              >
                <span className="b2bdtc-option-title">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BDTCSpecifications;
